import { AwsS3APIConstant } from './../constants/apis/aws-s3/AwsS3-api.constant';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { SpinnerState } from './../states/spinner.state';
import { CargueAPIConstant } from './../constants/apis/cargue/cargue-api.constant';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent
} from '@angular/common/http';
import { EscrutinioAPIConstant } from '../constants/apis/escrutinio/escrutinio-api.constant';
import { ParametrizacionesAPIConstant } from '../constants/apis/parametrizaciones/parametrizaciones-api-constant';
import { SessionStoreUtil } from '../utilities/session-store.util';
import { TipoEventoConstant } from '../constants/tipo-evento.constant';
import { AutenticacionAPIConstant } from '../constants/apis/seguridad/autenticacion-api.constant';
import { AprobacionConfiguracionImpresionAPIConstant } from '../constants/apis/administracion/papeleria/aprobacion-configuracion-impresion/aprobacion-configuracion-impresion-api-constant';
import { ConfiguracionContableProductosApiConstant } from '../constants/apis/administracion/control-operacion/configuracion-contable-productos.constant';
import { ConfiguracionCargueArchivoConvenioApiConstant } from '../constants/apis/administracion/cargue-archivos-convenio/cargue-archivos-convenio.api.constant';
import { ConfiguracionCargueArchivoBatchApiConstant } from '../constants/apis/administracion/cargue-archivo-batch/cargue-archivos-batch.api.constant';
import { NovedadesAPIConstant } from '../constants/apis/servicios/giros-nacionales/novedades/novedades-api.constant';
import { SolicitudesAPIConstant } from '../constants/apis/servicios/giros-nacionales/solicitud/solicitudes-api.constant';
import { EXCLUDED_URL_PATTERNS } from '../constants/apis/excluded_urls_loader/excluded_urls_loader.constant';
import { AuthTokenService } from '../modules/login/auth.service';
import { SessionEventService } from '../modules/login/session-event.service';
import { ConfiguracionSorteoAPIConstant } from '../constants/apis/gestor-sorteo-virtual/configuracion-sorteo/configuracion-sorteo.constant';
import { TipoBalotaAPIConstant } from '../constants/apis/gestor-sorteo-virtual/tipo-balota/tipo-balota.constant';

type ObjectInterceptor = {
  'Content-Type': string;
  Authorization?: string;
}

/**
 * Interceptor que permite configurar la seguridad y el spinner para
 * cada peticion HTTP que realiza el usuario
 */
@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {

  /**
   * @param spinnerState, se utiliza para visualizar, ocultar el spinner
   */
  constructor(private spinnerState: SpinnerState,
    private authTokenService: AuthTokenService,
    private sessionEventService: SessionEventService) { }

  /**
   * Metodo que permite capturar cada request del sistema,
   * para asi agregar la seguridad correspondiente a cada peticion
   * con su respectivo spinner
   *
   * @param req, es la solicitud que envia el cliente
   * @param next, es el siguiente interceptor a ejecutar, si aplica
   * @returns Observador con el request modificado
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (req.headers.has('skipInterceptor')) {
      this.spinnerState.displaySpinner();
      const headers = req.headers.delete('skipInterceptor');
      const newReq = req.clone({ headers });
      return next.handle(newReq).pipe(catchError((error) => {
        throw error;
      }),
      finalize(() => {
          this.spinnerState.hideSpinner();
      }));
    }

    // se configura el spinner para esta peticion
    let contentHeader;

    // Eliminar los parámetros de consulta (query params) antes de la comparación
    const cleanUrl = req.url.split('?')[0];

    // Verificar si la URL y el método están en la lista de exclusión
    const isExcluded = EXCLUDED_URL_PATTERNS.some(
      ({ pattern, method }) => pattern.test(cleanUrl) && method === req.method
    );

    if (!isExcluded) {
      this.spinnerState.displaySpinner();
    }

    if (CargueAPIConstant.URL_CARGUE_ARCHIVO_LOTERIA_VIRTUAL !== req.url
      && AwsS3APIConstant.URL_CARGUE_MANUAL !== req.url && EscrutinioAPIConstant.URL_SAVE_IMAGENES !== req.url      
      && !ConfiguracionSorteoAPIConstant.getAllEndpoints().some(endpoint => req.url === endpoint)
      && !TipoBalotaAPIConstant.getAllEndpoints().some(endpoint => req.url === endpoint)        
      && !req.url.includes(ParametrizacionesAPIConstant.URL_LOAD_RESOURCE) && !req.url.includes(ConfiguracionContableProductosApiConstant.URL_LOAD_RESOURCE) 
      && !ConfiguracionCargueArchivoConvenioApiConstant.getAllEndpoints().some(endpoint => req.url === endpoint)
      && !ConfiguracionCargueArchivoBatchApiConstant.getAllEndpoints().some(endpoint => req.url.includes(endpoint))
      && !req.url.includes(NovedadesAPIConstant.URL_NOVELTY) && !req.url.includes(SolicitudesAPIConstant.URL_SOLICITUDE)
      && !req.url.includes(AprobacionConfiguracionImpresionAPIConstant.URL_GET_COMPROBANTE_FISICO)
    ) {
      contentHeader = this.getOnlyTypeJson();
    } else {
      contentHeader = this.buildAuthHeader();
    }
    if (AutenticacionAPIConstant.URL_REFRESH_TOKEN == req.url) {
      contentHeader = this.getOnlyTypeJson();
    }
    return next.handle(req.clone({ setHeaders: contentHeader })).pipe(
      catchError((error) => {
        const isLogin = req.url === AutenticacionAPIConstant.URL_LOGIN;
        const isRefresh = req.url === AutenticacionAPIConstant.URL_REFRESH_TOKEN;
        const isSignout = req.url === AutenticacionAPIConstant.URL_SIGNOUT;
        const isVerifyPassword = req.url === AutenticacionAPIConstant.URL_VERIFY_PASSWORD;

        if (error.status === 401 && !isLogin && !isRefresh && !isSignout && !isVerifyPassword) {
          this.authTokenService.clearSession();
          this.sessionEventService.notifySessionInvalid();
        }
        throw error;
      }),
      finalize(() => {
        if (!isExcluded) {
          this.spinnerState.hideSpinner();
        }
      })
    );
  }

  /**
   * Metodo que permite crear un Header con solo el tipo de json content
   */
  private getOnlyTypeJson(): any {
    let object: ObjectInterceptor = {
      'Content-Type': 'application/json;charset=UTF-8'
    };

    const auth = SessionStoreUtil.auth(TipoEventoConstant.GET);
    if (auth?.idToken) {
      object = {
        ...object,
        'Authorization': `Bearer ${auth.idToken}`
      };
      return object;
    }

    const tempToken = this.authTokenService.getTemporaryToken();
    if (tempToken) {
      object = {
        ...object,
        'Authorization': `Bearer ${tempToken}`
      };
    }

    return object;
  }

  /**
   * Metodo que permite crear un Header sin definir el tipo de content (para multipart, pdf, etc)
   */
  private buildAuthHeader(): any {
    const auth = SessionStoreUtil.auth(TipoEventoConstant.GET);
    if (auth?.idToken) {
      return { Authorization: `Bearer ${auth.idToken}` };
    }
  
    const tempToken = this.authTokenService.getTemporaryToken();
    return tempToken ? { Authorization: `Bearer ${tempToken}` } : {};
  }
}
